import * as stream from "node:stream";

import type {
  AiChatNode,
  AiChatState,
  PickType,
  ToolResultOutput,
  ToolStep,
  UiMessageChunkDto,
  UiStreamAction,
  UiStreamHooks,
} from "@intelligems/ig-types";

import { UiMessageChunkTypeGuardProcessor } from "./ui-message-chunk-type-guard-processor";
import {
  getOrCreateNodeMessageById,
  getPartByToolCallIdAndMessageId,
} from "./utils";

export class ToolProcessor {
  static isToolPart(
    part: UiMessageChunkDto
  ): part is
    | PickType<UiMessageChunkDto, "tool-input-start">
    | PickType<UiMessageChunkDto, "tool-input-delta">
    | PickType<UiMessageChunkDto, "tool-input-available">
    | PickType<UiMessageChunkDto, "tool-input-error">
    | PickType<UiMessageChunkDto, "tool-output-available">
    | PickType<UiMessageChunkDto, "tool-output-error"> {
    return (
      UiMessageChunkTypeGuardProcessor.isToolInputStartChunk(part) ||
      UiMessageChunkTypeGuardProcessor.isToolInputDeltaChunk(part) ||
      UiMessageChunkTypeGuardProcessor.isToolInputAvailableChunk(part) ||
      UiMessageChunkTypeGuardProcessor.isToolInputErrorChunk(part) ||
      UiMessageChunkTypeGuardProcessor.isToolOutputAvailableChunk(part) ||
      UiMessageChunkTypeGuardProcessor.isToolOutputErrorChunk(part)
    );
  }

  static processStreamPart(
    draft: AiChatState,
    node: AiChatNode,
    messageId: string,
    streamPart: UiMessageChunkDto,
    hooks?: UiStreamHooks
  ): void {
    if (UiMessageChunkTypeGuardProcessor.isToolInputStartChunk(streamPart)) {
      this.toolInputStart(node, messageId, streamPart);
    } else if (
      UiMessageChunkTypeGuardProcessor.isToolInputDeltaChunk(streamPart)
    ) {
      this.concatToolInputDelta(node, messageId, streamPart);
    } else if (
      UiMessageChunkTypeGuardProcessor.isToolInputAvailableChunk(streamPart)
    ) {
      this.toolInputAvailable(node, messageId, streamPart, hooks);
    } else if (
      UiMessageChunkTypeGuardProcessor.isToolInputErrorChunk(streamPart)
    ) {
      this.toolInputError(node, messageId, streamPart);
    } else if (
      UiMessageChunkTypeGuardProcessor.isToolOutputAvailableChunk(streamPart)
    ) {
      this.toolOutputAvailable(draft, node, messageId, streamPart, hooks);
    } else if (
      UiMessageChunkTypeGuardProcessor.isToolOutputErrorChunk(streamPart)
    ) {
      this.toolOutputError(node, messageId, streamPart);
    }
  }

  private static toolInputStart(
    node: AiChatNode,
    messageId: string,
    streamPart: PickType<UiMessageChunkDto, "tool-input-start">
  ) {
    const existingPart = getPartByToolCallIdAndMessageId(
      node.messages,
      messageId,
      streamPart.toolCallId
    );
    if (existingPart) {
      return;
    }

    const message = getOrCreateNodeMessageById(
      node,
      messageId,
      "assistant",
      streamPart.timestamp
    );

    message.parts.push({
      type: "dynamic-tool",
      toolCallId: streamPart.toolCallId,
      toolName: streamPart.toolName,
      input: "",
      output: undefined,
      state: "input-streaming",
      timestamp: streamPart.timestamp,
    });
  }

  private static concatToolInputDelta(
    node: AiChatNode,
    messageId: string,
    streamPart: PickType<UiMessageChunkDto, "tool-input-delta">
  ) {
    const part = getPartByToolCallIdAndMessageId(
      node.messages,
      messageId,
      streamPart.toolCallId
    );

    if (part && part.type === "dynamic-tool") {
      if (typeof part.input === "string") {
        part.input += streamPart.inputTextDelta;
      }
      part.state = "input-streaming";
      part.timestamp = streamPart.timestamp;
    }
  }

  /**
   * Fold a reported step onto the call it belongs to.
   *
   * Upsert by id, in first-seen order: a tool reports a step as `running` and
   * again when it settles, and the card has to show one row moving rather than
   * two rows appearing. A step for a call we have no part for is dropped —
   * progress is narration, and narrating something not on screen is nothing.
   */
  static handleToolProgress(
    node: AiChatNode,
    payload: Extract<UiStreamAction, { type: "addToolProgress" }>
  ) {
    const part = getPartByToolCallIdAndMessageId(
      node.messages,
      payload.messageId,
      payload.toolCallId
    );
    if (!part || part.type !== "dynamic-tool") {
      return;
    }

    const steps: ToolStep[] = part.steps ?? [];
    const at = steps.findIndex((it) => it.id === payload.step.id);

    if (at === -1) {
      steps.push(payload.step);
    } else {
      steps[at] = payload.step;
    }
    part.steps = steps;
  }

  private static toolInputAvailable(
    node: AiChatNode,
    messageId: string,
    streamPart: PickType<UiMessageChunkDto, "tool-input-available">,
    hooks?: UiStreamHooks
  ) {
    const part = getPartByToolCallIdAndMessageId(
      node.messages,
      messageId,
      streamPart.toolCallId
    );

    if (part && part.type === "dynamic-tool") {
      part.input = streamPart.input;
      part.state = "input-available";
      part.timestamp = streamPart.timestamp;

      hooks?.onToolInputAvailable?.(part.input as any);
      return undefined;
    }

    // Replayed/persisted streams send `tool-input-available` without a
    // preceding `tool-input-start` (the input was never streamed).
    const message = getOrCreateNodeMessageById(
      node,
      messageId,
      "assistant",
      streamPart.timestamp
    );

    message.parts.push({
      type: "dynamic-tool",
      toolCallId: streamPart.toolCallId,
      toolName: streamPart.toolName,
      input: streamPart.input,
      output: undefined,
      state: "input-available",
      timestamp: streamPart.timestamp,
    });

    hooks?.onToolInputAvailable?.(streamPart.input as any);
    return undefined;
  }

  private static toolInputError(
    node: AiChatNode,
    messageId: string,
    streamPart: PickType<UiMessageChunkDto, "tool-input-error">
  ) {
    const part = getPartByToolCallIdAndMessageId(
      node.messages,
      messageId,
      streamPart.toolCallId
    );

    if (part && part.type === "dynamic-tool") {
      part.state = "output-error";
      part.timestamp = streamPart.timestamp;
      part.output = { errorMessage: streamPart.errorText };
    }
  }

  private static toolOutputAvailable(
    draft: AiChatState,
    node: AiChatNode,
    messageId: string,
    streamPart: PickType<UiMessageChunkDto, "tool-output-available">,
    hooks?: UiStreamHooks
  ) {
    const part = getPartByToolCallIdAndMessageId(
      node.messages,
      messageId,
      streamPart.toolCallId
    );

    if (part && part.type === "dynamic-tool") {
      part.output = streamPart.output;
      part.state = "output-available";
      part.timestamp = streamPart.timestamp;

      // A `request-user-input` result is NOT the park signal. Its output
      // streams the moment the tool returns, but the server is still busy
      // after that: the rest of the step (any tool the model called alongside
      // the ask), the node-end hooks, and the session/context persist all run
      // before the turn parks. Surfacing the question here put the card on
      // screen while the stream was still open and the panel disabled — the
      // options looked clickable and swallowed every click. The question
      // lands on `nextAction` from the server's `setNextAction`, pushed once
      // the turn is actually parked and immediately before the stream closes.
      if (part.toolName !== "request-user-input") {
        hooks?.onToolOutputAvailable?.(part.output as ToolResultOutput);
      }
    }
    return undefined;
  }

  private static toolOutputError(
    node: AiChatNode,
    messageId: string,
    streamPart: PickType<UiMessageChunkDto, "tool-output-error">
  ) {
    const part = getPartByToolCallIdAndMessageId(
      node.messages,
      messageId,
      streamPart.toolCallId
    );

    if (part && part.type === "dynamic-tool") {
      part.state = "output-error";
      part.timestamp = streamPart.timestamp;
      part.output = { errorMessage: streamPart.errorText };
    }
    return undefined;
  }
}
